// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Jefter Ruthes - https://ruthes.dev

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import PropTypes from 'prop-types';
import { PRESET_COLORS, getRoleBadgeStyles } from '../utils/roleColors';

// =================================================================================
// COMPONENTE: Node de Rede (Recursivo)
// =================================================================================
const NetworkNode = ({ member, level = 0, onMove }) => {
  const [expanded, setExpanded] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const hasChildren = member.children && member.children.length > 0;

  const handleDragStart = (e) => {
    e.stopPropagation();
    e.dataTransfer.setData('text/plain', String(member.id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const draggedId = e.dataTransfer.getData('text/plain');

    if (draggedId && String(draggedId) !== String(member.id) && onMove) {
      onMove(draggedId, member.id);
    }
  };

  return (
    <div
      className="network-node"
      style={{ marginLeft: level > 0 ? '2rem' : '0', marginTop: '0.5rem' }}
    >
      <div
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0.75rem',
          backgroundColor: isDragOver ? 'var(--primary-light)' : 'var(--surface)',
          border: isDragOver ? '2px dashed var(--primary)' : '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          gap: '0.75rem',
          position: 'relative',
          cursor: 'move',
          transition: 'all 0.2s ease'
        }}
      >
        {level > 0 && (
          <div style={{
            position: 'absolute',
            left: '-2rem',
            top: '50%',
            width: '2rem',
            height: '1px',
            backgroundColor: 'var(--border)',
            zIndex: 0
          }}></div>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          style={{
            border: 'none',
            background: 'none',
            cursor: hasChildren ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            color: 'var(--text-muted)',
            visibility: hasChildren ? 'visible' : 'hidden'
          }}
        >
          <span className="material-symbols-outlined">
            {expanded ? 'expand_more' : 'chevron_right'}
          </span>
        </button>

        <div style={{
          width: '32px', height: '32px', borderRadius: '50%',
          backgroundColor: member.is_active ? 'var(--primary-light)' : 'var(--surface-hover)',
          backgroundImage: member.photo_thumbnail_url ? `url(${member.photo_thumbnail_url})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--primary)', fontWeight: 'bold', fontSize: '0.8rem',
          border: '1px solid var(--border-light)'
        }}>
          {!member.photo_thumbnail_url && member.full_name.charAt(0)}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{member.full_name}</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {member.roles && member.roles.length > 0 ? (
              <span>{member.roles.map(r => r.name).join(', ')}</span>
            ) : (
              <span style={{ fontStyle: 'italic' }}>Sem cargo</span>
            )}
            •
            <span>{member.children.length} liderados</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', padding: '2px 6px', background: 'var(--surface-hover)', borderRadius: '4px' }}>
              ID: {member.id}
            </span>
          </div>
        </div>
      </div>

      {hasChildren && expanded && (
        <div className="network-children" style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute',
            left: '0.9rem', // Alinha com o botão de expandir
            top: 0,
            bottom: '1rem',
            width: '1px',
            backgroundColor: 'var(--border)',
            zIndex: 0
          }}></div>
          {member.children.map(child => (
            <NetworkNode key={child.id} member={child} level={level + 1} onMove={onMove} />
          ))}
        </div>
      )}
    </div>
  );
};

NetworkNode.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    full_name: PropTypes.string.isRequired,
    is_active: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    photo_thumbnail_url: PropTypes.string,
    children: PropTypes.array,
    roles: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }))
  }).isRequired,
  level: PropTypes.number,
  onMove: PropTypes.func
};

// =================================================================================
// COMPONENTE: Node de Membro Não Associado (Draggable)
// =================================================================================
const UnassignedMemberNode = ({ member, onDragStart, onDrop }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, member)}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const draggedId = e.dataTransfer.getData('text/plain');
        if (draggedId && String(draggedId) !== String(member.id) && onDrop) {
          onDrop(draggedId, member.id);
        }
      }}
      style={{
        padding: '0.75rem',
        backgroundColor: isDragOver ? 'var(--primary-light)' : 'var(--surface)',
        border: isDragOver ? '2px dashed var(--primary)' : '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        marginBottom: '0.5rem',
        cursor: 'grab',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{
        width: '32px', height: '32px', borderRadius: '50%',
        backgroundColor: member.is_active ? 'var(--primary-light)' : 'var(--surface-hover)',
        backgroundImage: member.photo_thumbnail_url ? `url(${member.photo_thumbnail_url})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--primary)', fontWeight: 'bold', fontSize: '0.8rem',
        border: '1px solid var(--border-light)'
      }}>
        {!member.photo_thumbnail_url && member.full_name.charAt(0)}
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ fontWeight: '600', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {member.full_name}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {member.roles && member.roles.length > 0 ? member.roles.map(r => r.name).join(', ') : 'Sem cargo'}
        </div>
      </div>
      <span className="material-symbols-outlined" style={{ color: 'var(--text-muted)', fontSize: '1.2rem' }}>
        drag_indicator
      </span>
    </div>
  );
};

UnassignedMemberNode.propTypes = {
  member: PropTypes.object.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDrop: PropTypes.func
};

// =================================================================================
// COMPONENTE: HIERARQUIA DE REDE (PESSOAS)
// =================================================================================
const NetworkHierarchyView = () => {
  const queryClient = useQueryClient();

  // Busca apenas membros com funções de liderança
  const { data: membersData, isLoading } = useQuery({
    queryKey: ['members', 'leaders-only'],
    queryFn: async () => {
      // Lista de roles que definem liderança
      const leadershipRoles = ['lider_celula', 'discipulador_celula', 'pastor_rede', 'pastor_senior', 'pastor_supervisor'];
      const params = {
        per_page: 999,
        role: leadershipRoles.join(',')
      };
      const response = await api.get('/members', { params });
      return response.data.data;
    },
  });

  const moveMutation = useMutation({
    mutationFn: async ({ memberId, supervisorId }) => {
      return api.put(`/members/${memberId}`, { supervisor_id: supervisorId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (error) => {
      alert('Erro ao mover membro: ' + (error.response?.data?.message || error.message));
    }
  });

  const handleMove = (item, targetId) => {
    if (String(item) === String(targetId)) return;

    // Previne a criação de novos ciclos (não permite associar a um próprio liderado)
    let currentId = targetId;
    let hasCycle = false;
    while (currentId) {
      if (String(currentId) === String(item)) {
        hasCycle = true;
        break;
      }
      const currentMember = membersData?.find(m => String(m.id) === String(currentId));
      currentId = currentMember ? currentMember.supervisor_id : null;
    }

    if (hasCycle) {
      setTimeout(() => {
        alert('Não é possível associar a este líder, pois geraria um ciclo na hierarquia (ele já é seu liderado direto ou indireto).');
      }, 10);
      return;
    }

    moveMutation.mutate({ memberId: item, supervisorId: targetId });
  };

  const handleUnassign = (item) => {
    moveMutation.mutate({ memberId: item, supervisorId: null });
  };

  const { hierarchy, unassigned } = useMemo(() => {
    if (!membersData) return { hierarchy: [], unassigned: [] };

    const memberMap = {};
    const unassignedList = [];
    const roots = [];

    // Primeiro cria mapa para acesso rápido e inicializa children
    membersData.forEach(m => {
      memberMap[m.id] = { ...m, children: [] };
    });

    // Identifica e quebra ciclos existentes para que os membros não desapareçam
    const visited = new Set();
    const inPath = new Set();
    
    membersData.forEach(m => {
      let currentId = m.id;
      const path = [];
      let cycleStartId = null;

      while (currentId && memberMap[currentId]) {
        const strId = String(currentId);
        if (inPath.has(strId)) {
          cycleStartId = currentId;
          break;
        }
        if (visited.has(strId)) break;
        inPath.add(strId);
        path.push(strId);
        currentId = memberMap[currentId].supervisor_id;
      }

      if (cycleStartId) {
        // Quebra o ciclo desassociando o membro que originou/fechou o loop na leitura visual
        memberMap[cycleStartId].supervisor_id = null;
      }

      path.forEach(id => {
        inPath.delete(id);
        visited.add(id);
      });
    });

    // Constrói a árvore
    membersData.forEach(m => {
      const node = memberMap[m.id];
      if (node.supervisor_id && memberMap[node.supervisor_id]) {
        memberMap[node.supervisor_id].children.push(node);
      }
    });

    // Segundo pass para separar Roots vs Unassigned
    membersData.forEach(m => {
      const node = memberMap[m.id];
      // Se tem supervisor válido presente na lista, já foi adicionado como filho
      if (node.supervisor_id && memberMap[node.supervisor_id]) return;

      // Lógica de Bootstrap:
      // Se tem filhos, TEM que estar na árvore.
      // Se é um Pastor (Senior, Supervisor, Rede), assume-se que é raiz e deve estar na árvore.
      // Caso contrário (Líder de Célula, Discipulador sem líder), vai para sidebar.

      const isTopLevelRole = node.roles && node.roles.some(r =>
        ['pastor_senior', 'pastor_supervisor', 'pastor_rede'].includes(r.slug)
      );

      if (node.children.length > 0 || isTopLevelRole) {
        roots.push(node);
      } else {
        // Se não tem supervisor, não tem filhos e não é alto escalão -> Sidebar
        unassignedList.push(node);
      }
    });

    // Ordena unassigned por nome
    unassignedList.sort((a, b) => a.full_name.localeCompare(b.full_name));

    return { hierarchy: roots, unassigned: unassignedList };
  }, [membersData]);

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="network-hierarchy-view" style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 2fr) minmax(300px, 1fr)',
      gap: '2rem',
      height: 'calc(100vh - 200px)',
      minHeight: '600px'
    }}>
      {/* Coluna Esquerda: Árvore Hierárquica */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--border)',
        paddingRight: '2rem',
        overflowY: 'auto'
      }}>
        <div className="hierarchy-header" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--primary)' }}>Rede de Pessoas</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Estrutura de liderança. Arraste líderes da direita para cá.
          </p>
        </div>

        <div 
          className="network-tree" 
          style={{ paddingBottom: '2rem', minHeight: '200px', transition: 'background-color 0.2s', borderRadius: 'var(--radius-lg)' }}
          onDragOver={(e) => {
            // Se soltar num nó, o evento do nó para a propagação.
            // Se soltar no espaço vazio, chega aqui.
            e.preventDefault();
            e.currentTarget.style.backgroundColor = 'var(--surface-hover)';
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.backgroundColor = 'transparent';
            const draggedId = e.dataTransfer.getData('text/plain');
            if (draggedId) {
              handleUnassign(draggedId);
            }
          }}
        >
          {hierarchy.length === 0 ? (
            <div className="alert" style={{ marginTop: '1rem' }}>
              Nenhuma rede formada ainda. Certifique-se de ter Pastores cadastrados ou arraste líderes para cá.
            </div>
          ) : (
            hierarchy.map(rootMember => (
              <NetworkNode key={rootMember.id} member={rootMember} onMove={handleMove} />
            ))
          )}
        </div>
      </div>

      {/* Coluna Direita: Líderes Disponíveis */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--surface-hover)',
          borderRadius: 'var(--radius-lg)',
          padding: '1rem',
          overflowY: 'auto',
          border: '2px dashed transparent',
          transition: 'all 0.2s'
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = 'var(--primary)';
          e.currentTarget.style.backgroundColor = 'var(--primary-light-10)';
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.backgroundColor = 'var(--surface-hover)';
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.backgroundColor = 'var(--surface-hover)';
          const draggedId = e.dataTransfer.getData('text/plain');
          if (draggedId) {
            handleUnassign(draggedId);
          }
        }}
      >
        <div className="sidebar-header" style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--text)' }}>Líderes Disponíveis</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
            Arraste para cá para remover a liderança (desvincular).
          </p>
        </div>

        <div className="unassigned-list">
          {unassigned.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem 0' }}>
              Todos os líderes estão associados.
            </div>
          ) : (
            unassigned.map(member => (
              <UnassignedMemberNode
                key={member.id}
                member={member}
                onDragStart={(e, m) => {
                  e.dataTransfer.setData('text/plain', String(m.id));
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDrop={(draggedId, targetId) => {
                  handleMove(draggedId, targetId);
                }}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// =================================================================================
// COMPONENTE: ESTRUTURA DE CÉLULAS
// =================================================================================
const CellHierarchyView = () => {
  // Carrega células
  const { data: cellsData, isLoading: loadingCells } = useQuery({
    queryKey: ['cells'],
    queryFn: async () => {
      const response = await api.get('/cells');
      return response.data.data;
    },
  });

  // Carrega membros
  const { data: membersData, isLoading: loadingMembers } = useQuery({
    queryKey: ['members'],
    queryFn: async () => {
      const response = await api.get('/members', { params: { per_page: 999 } });
      return response.data.data;
    },
  });

  if (loadingCells || loadingMembers) {
    return <LoadingSpinner />;
  }

  const cells = cellsData || [];
  const members = membersData || [];

  // Helper para encontrar o líder de uma célula
  const getCellLeader = (cellId) => {
    return members.find(m => m.cells?.some(c => c.id === cellId && c.is_leader));
  };

  // Helper para encontrar membros de uma célula (excluindo o líder)
  const getCellMembers = (cellId) => {
    return members.filter(m => m.cells?.some(c => c.id === cellId && !c.is_leader));
  };

  return (
    <div className="hierarchy-view" style={{ padding: '1rem' }}>
      <div className="hierarchy-header" style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--primary)' }}>Estrutura de Células</h3>
        <p style={{ color: 'var(--text-muted)' }}>Organização das células e seus membros.</p>
      </div>

      <div className="org-tree" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {cells.length === 0 ? (
          <div className="alert">Nenhuma célula cadastrada.</div>
        ) : (
          cells.map(cell => {
            const leader = getCellLeader(cell.id);
            const cellMembers = getCellMembers(cell.id);

            // Implementação simples de Accordion visual
            return (
              <details key={cell.id} className="cell-accordion" style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                backgroundColor: 'var(--surface)',
                overflow: 'hidden'
              }}>
                <summary style={{
                  padding: '1rem 1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: '600',
                  listStyle: 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div className="icon-box" style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      backgroundColor: 'var(--primary-light)', color: 'var(--primary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>groups</span>
                    </div>
                    <span>{cell.name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      {leader ? `Líder: ${leader.full_name}` : 'Sem líder'} • {cellMembers.length} membros
                    </span>
                    <span className="material-symbols-outlined summary-icon">expand_more</span>
                  </div>
                </summary>

                <div style={{ padding: '0 1.5rem 1.5rem 1.5rem', borderTop: '1px solid var(--border-light)', marginTop: '0.5rem', paddingTop: '1rem' }}>
                  {/* Líder */}
                  <div style={{ marginBottom: '1rem' }}>
                    <h5 style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Líder da Célula</h5>
                    {leader ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="material-symbols-outlined" style={{ color: 'var(--primary)', fontSize: '1.2rem' }}>verified_user</span>
                        <span>{leader.full_name}</span>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--danger)', fontStyle: 'italic' }}>Não atribuído</span>
                    )}
                  </div>

                  {/* Membros */}
                  <div>
                    <h5 style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Participantes</h5>
                    {cellMembers.length > 0 ? (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                        {cellMembers.map(m => (
                          <li key={m.id} style={{ padding: '0.5rem', backgroundColor: 'var(--surface-hover)', borderRadius: 'var(--radius-sm)', fontSize: '0.9rem' }}>
                            {m.full_name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>Sem participantes adicionais.</span>
                    )}
                  </div>
                </div>
              </details>
            );
          })
        )}
      </div>
      <style>{`
        details > summary { list-style: none; }
        details > summary::-webkit-details-marker { display: none; }
        details[open] .summary-icon { transform: rotate(180deg); }
        .summary-icon { transition: transform 0.2s ease; }
      `}</style>
    </div>
  );
};

// =================================================================================
// COMPONENTE: LISTA DE FUNÇÕES (CRUD INLINE)
// =================================================================================
const RolesListView = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '', hierarchy_level: 1, permissions: ['level:dashboard_principal'], color: '' });

  // Carrega roles
  const { data: rolesData, isLoading: loadingRoles } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await api.get('/roles');
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (newRole) => api.post('/roles', newRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/roles/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (roleId) => api.delete(`/roles/${roleId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] })
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingRole(null);
    setFormData({ name: '', slug: '', description: '', hierarchy_level: 1, permissions: ['level:dashboard_principal'], color: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      slug: role.slug,
      description: role.description || '',
      hierarchy_level: role.hierarchy_level || 1,
      permissions: role.permissions && role.permissions.length > 0 ? role.permissions : ['level:dashboard_principal'],
      color: role.color || ''
    });
    setShowForm(true);
    // Rola para o topo suavemente para ver o formulário
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (roleId) => {
    if (window.confirm('Tem certeza? Isso removerá a função de todos os membros.')) {
      deleteMutation.mutate(roleId);
    }
  };

  if (loadingRoles) return <LoadingSpinner />;

  return (
    <div className="roles-list-view">
      <div className="actions-bar" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        {!showForm && (
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            type="button"
          >
            <span className="material-symbols-outlined">add</span> Nova Função
          </button>
        )}
      </div>

      {/* FORMULÁRIO INLINE (Substituição do Modal) */}
      {showForm && (
        <div className="card" style={{
          padding: '2rem',
          marginBottom: '2rem',
          border: '2px solid var(--primary-light)',
          backgroundColor: 'var(--surface)'
        }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span className="material-symbols-outlined">{editingRole ? 'edit' : 'add_circle'}</span>
            {editingRole ? 'Editar Função' : 'Nova Função'}
          </h3>

          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Nome</label>
                <input
                  className="form-input"
                  required
                  placeholder="Ex: Líder de Célula"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Slug (ID Único)</label>
                <input
                  className="form-input"
                  required
                  placeholder="Ex: lider_celula"
                  value={formData.slug}
                  onChange={e => setFormData({ ...formData, slug: e.target.value })}
                  readOnly={!!editingRole}
                  style={{ width: '100%', padding: '0.75rem', backgroundColor: editingRole ? 'var(--surface-hover)' : 'white' }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Descrição</label>
              <textarea
                className="form-input"
                placeholder="Descreva as responsabilidades desta função..."
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', minHeight: '80px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Nível de Acesso ao Sistema</label>
              <select
                className="form-input"
                required
                value={formData.permissions[0] || 'level:dashboard_principal'}
                onChange={e => setFormData({ ...formData, permissions: [e.target.value] })}
                style={{ width: '100%', padding: '0.75rem', backgroundColor: 'var(--surface)' }}
              >
                <option value="level:full_access">Acesso Total (Ver todos os dados do Tenant)</option>
                <option value="level:dashboard_principal_gestao_sistema">Acesso aos Menus: Principal, Gestão e Sistema (Filtra dados pela Rede)</option>
                <option value="level:dashboard_principal_gestao">Acesso aos Menus: Principal e Gestão (Filtra dados pela Rede)</option>
                <option value="level:dashboard_principal">Acesso aos Menus: Principal (Filtra dados pela Rede)</option>
              </select>
              <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                Define os menus que o usuário desta função poderá ver na barra lateral, e regra de visibilidade dos dados.
              </small>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Nível Hierárquico (1 = Mais Alto)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <input
                  type="number"
                  min="1"
                  className="form-input"
                  required
                  value={formData.hierarchy_level}
                  onChange={e => setFormData({ ...formData, hierarchy_level: parseInt(e.target.value) })}
                  style={{ width: '100px', padding: '0.75rem' }}
                />
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  Defina a ordem de autoridade. 1: Pastor, 2: Supervisor, 3: Líder...
                </span>
              </div>
            </div>

            {!formData.is_system && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Cor da Função</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: c })}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: c,
                        border: formData.color === c ? '3px solid var(--text-primary)' : '1px solid var(--border)',
                        cursor: 'pointer',
                        boxShadow: formData.color === c ? '0 0 0 2px white inset' : 'none',
                        transition: 'transform 0.1s ease',
                        transform: formData.color === c ? 'scale(1.1)' : 'scale(1)',
                      }}
                      title={c}
                    />
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginLeft: '0.5rem' }}>
                    <input
                      type="color"
                      value={formData.color && PRESET_COLORS.includes(formData.color) ? '#000000' : formData.color || '#4F46E5'}
                      onChange={e => setFormData({ ...formData, color: e.target.value })}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        border: '1px solid var(--border)',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        overflow: 'hidden'
                      }}
                    />
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Personalizada</span>
                  </div>
                  {formData.color && (
                    <button
                      type="button"
                      className="btn btn-text btn-sm"
                      onClick={() => setFormData({ ...formData, color: '' })}
                      style={{ marginLeft: 'auto', padding: '0.25rem' }}
                    >
                      Limpar cor
                    </button>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-light)' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
                style={{ padding: '0.75rem 1.5rem' }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <span className="material-symbols-outlined">save</span>
                {editingRole ? 'Salvar Alterações' : 'Criar Função'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* LISTAGEM */}
      <div className="roles-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
        {rolesData?.map(role => (
          <div key={role.id} className="card" style={{
            padding: '1.5rem',
            borderLeft: `4px solid ${role.color || getRoleBadgeStyles(role).color || 'var(--primary)'}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
              <h4 style={{ fontWeight: 'bold', fontSize: '1.1rem', margin: 0 }}>{role.name}</h4>
              {!role.is_system && (
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button className="btn-icon" onClick={() => handleEdit(role)} title="Editar">
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                  <button className="btn-icon text-danger" onClick={() => handleDelete(role.id)} title="Excluir">
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
              )}
            </div>

            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontFamily: 'monospace' }}>
              {role.slug}
            </div>

            <p style={{ fontSize: '0.9rem', marginBottom: '1rem', minHeight: '3rem', color: 'var(--text-secondary)' }}>
              {role.description || 'Sem descrição.'}
            </p>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.8rem',
              backgroundColor: 'var(--surface-hover)',
              padding: '0.25rem 0.75rem',
              borderRadius: '99px',
              fontWeight: '500',
              marginBottom: '0.5rem'
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>military_tech</span>
              Nível {role.hierarchy_level}
            </div>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.8rem',
              backgroundColor: role.permissions && role.permissions.includes('level:full_access') ? 'var(--danger-light)' : 'var(--info-light)',
              color: role.permissions && role.permissions.includes('level:full_access') ? 'var(--danger)' : 'var(--info)',
              padding: '0.25rem 0.75rem',
              borderRadius: '99px',
              fontWeight: '500',
              marginLeft: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>
                {role.permissions && role.permissions.includes('level:full_access') ? 'admin_panel_settings' : 'shield_person'}
              </span>
              {role.permissions && role.permissions[0] ? role.permissions[0].replace('level:', '').replace(/_/g, ' ').toUpperCase() : 'SEM ACESSO'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =================================================================================
// PÁGINA PRINCIPAL
// =================================================================================
function RolesPage() {
  const [activeTab, setActiveTab] = useState('networks'); // 'networks' | 'cells' | 'list'

  return (
    <AppLayout>
      <div className="roles-page" style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <div className="page-header" style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold' }}>Redes e Funções</h1>
          <p style={{ color: 'var(--text-muted)' }}>Gerencie a estrutura de pessoas, células e os cargos da igreja.</p>
        </div>

        {/* Navegação de Abas */}
        <div className="tabs" style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid var(--border)', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <button
            className={`tab-btn ${activeTab === 'networks' ? 'active' : ''}`}
            onClick={() => setActiveTab('networks')}
            style={{
              padding: '1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: activeTab === 'networks' ? 'bold' : 'normal',
              borderBottom: activeTab === 'networks' ? '3px solid var(--primary)' : '3px solid transparent',
              color: activeTab === 'networks' ? 'var(--primary)' : 'var(--text-muted)',
              display: 'flex', alignItems: 'center'
            }}
          >
            <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>hub</span>
            Redes (Pessoas)
          </button>

          <button
            className={`tab-btn ${activeTab === 'cells' ? 'active' : ''}`}
            onClick={() => setActiveTab('cells')}
            style={{
              padding: '1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: activeTab === 'cells' ? 'bold' : 'normal',
              borderBottom: activeTab === 'cells' ? '3px solid var(--primary)' : '3px solid transparent',
              color: activeTab === 'cells' ? 'var(--primary)' : 'var(--text-muted)',
              display: 'flex', alignItems: 'center'
            }}
          >
            <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>account_tree</span>
            Estrutura (Células)
          </button>

          <button
            className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
            style={{
              padding: '1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: activeTab === 'list' ? 'bold' : 'normal',
              borderBottom: activeTab === 'list' ? '3px solid var(--primary)' : '3px solid transparent',
              color: activeTab === 'list' ? 'var(--primary)' : 'var(--text-muted)',
              display: 'flex', alignItems: 'center'
            }}
          >
            <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>list</span>
            Lista de Funções
          </button>
        </div>

        {/* Conteúdo da Aba Ativa */}
        <div className="tab-content">
          {activeTab === 'networks' && <NetworkHierarchyView />}
          {activeTab === 'cells' && <CellHierarchyView />}
          {activeTab === 'list' && <RolesListView />}
        </div>
      </div>
    </AppLayout>
  );
}

export default RolesPage;
